/**
 * useBottomSheetGesture Hook
 *
 * Custom hook for handling bottom sheet drag gestures
 * Provides natural drag-to-close behavior for bottom sheets and overlays
 */

import { useDrag } from "@use-gesture/react";
import { useState } from "react";
import { clamp, hapticFeedback } from "@/lib/utils/gesture.utils";

interface UseBottomSheetGestureOptions {
	onClose: () => void;
	closeThreshold?: number;
	disabled?: boolean;
}

export function useBottomSheetGesture(options: UseBottomSheetGestureOptions) {
	const { onClose, closeThreshold = 150, disabled = false } = options;

	const [dragState, setDragState] = useState({
		y: 0,
		isDragging: false,
		opacity: 1,
	});

	const bind = useDrag(
		({
			active,
			movement: [, my],
			velocity: [, vy],
			direction: [, dy],
			cancel,
		}) => {
			if (disabled) return;

			// Only allow downward drags
			if (dy > 0 && my > 0) {
				// Calculate opacity based on drag distance
				const opacity = clamp(1 - my / 300, 0.5, 1);

				setDragState({
					y: my,
					isDragging: active,
					opacity,
				});

				// Check if should close
				if (!active) {
					if (my > closeThreshold || vy > 0.5) {
						hapticFeedback("light");
						onClose();
						cancel();
					} else {
						// Snap back
						setDragState({
							y: 0,
							isDragging: false,
							opacity: 1,
						});
					}
				}
			} else if (!active) {
				// Reset if gesture is not downward
				setDragState({
					y: 0,
					isDragging: false,
					opacity: 1,
				});
			}
		},
		{
			filterTaps: true,
			axis: "y",
			rubberband: true,
		},
	);

	return {
		dragState,
		bind,
	};
}
